
























import {
  ref, computed, defineComponent, reactive,
} from '@vue/composition-api';
import { useQuery } from '@vue/apollo-composable';

import {
  NOW_YEAR, thMonthsMini, timeframeSubtract, toCommaFormat,
} from '@/utils';

import ChartWithMoneyAxis from '@/components/Vivat/Chart/ChartWithMoneyAxis.vue';

const ChartWithLastYearCompare = defineComponent({
  components: {
    ChartWithMoneyAxis,
  },
  props: {
    primaryColor: {
      type: String,
      default: 'rgba(18,74,136,255)',
    },
    secondaryColor: {
      type: String,
      default: 'rgba(80,161,242,255)',
    },
    query: {
      default: undefined,
    },
    result: {
      type: Function,
      default: undefined,
    },
    lastYearResult: {
      type: Function,
      default: undefined,
    },
    title: {
      type: String,
      default: '',
    },
    formatYAxis: {
      type: Boolean,
      default: true,
    },
    showPercentageLine: { type: Boolean, default: false },
    yAxisLabel: { default: 'รายได้ (บาท)' },
  },
  setup(props) {
    const filter = reactive({
      year: {
        from: `${NOW_YEAR}-01-01`,
        to: `${NOW_YEAR}-12-31`,
      },
    });
    const max = ref(0);

    const { result, loading } = useQuery(
      props.query as any,
      computed(() => ({
        timeframe: filter.year,
        timeframeY1: timeframeSubtract(filter.year),
      })),
      () => ({
        fetchPolicy: 'network-only',
        enabled: props.query !== undefined && props.result !== undefined && props.lastYearResult !== undefined,
      }),
    );
    const rawData = computed(() => {
      if (loading.value === false && result.value && props.result !== undefined && props.lastYearResult !== undefined) {
        return {
          currentYear: props.result(result.value),
          lastYear: props.lastYearResult(result.value),
        };
      }
      return { currentYear: [], lastYear: [] };
    });
    const mapRawDataIntoFormat = computed(() => {
      const primaryCurrentYear = [];
      const secondaryCurrentYear = [];
      const primaryLastYear = [];
      const secondaryLastYear = [];
      let totalCurrentYear = 0;
      let totalLastYear = 0;
      for (let i = 0; i < rawData.value.currentYear.length; i += 1) {
        primaryCurrentYear.push(rawData.value.currentYear[i].fullTruckLoaded);
        secondaryCurrentYear.push(rawData.value.currentYear[i].lessThanTruckLoaded);

        const sum = (rawData.value.currentYear[i].fullTruckLoaded + rawData.value.currentYear[i].lessThanTruckLoaded);
        totalCurrentYear += sum;
        if (sum > max.value) max.value = sum;
      }
      for (let i = 0; i < rawData.value.lastYear.length; i += 1) {
        primaryLastYear.push(rawData.value.lastYear[i].fullTruckLoaded);
        secondaryLastYear.push(rawData.value.lastYear[i].lessThanTruckLoaded);

        const sum = (rawData.value.lastYear[i].fullTruckLoaded + rawData.value.lastYear[i].lessThanTruckLoaded);
        totalLastYear += sum;
        if (sum > max.value) max.value = sum;
      }
      return {
        primaryCurrentYear,
        secondaryCurrentYear,
        primaryLastYear,
        secondaryLastYear,
        totalCurrentYear,
        totalLastYear,
      };
    });

    const plugins = computed(() => ({
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const { dataIndex, dataset } = context || '';

            if (dataset?.label === 'Percentage') return `${toCommaFormat(context.dataset.data[dataIndex], 0, 0)}%`;

            return `${toCommaFormat(context.dataset.data[dataIndex], 0, 0)}`;
          },
        },
      },
    }));

    const percentage = computed(() => {
      if (!props.showPercentageLine) return { data: [], scales: {} };
      const data = [];
      for (let i = 0; i < mapRawDataIntoFormat.value.primaryCurrentYear.length; i += 1) {
        data.push(((mapRawDataIntoFormat.value.primaryCurrentYear[i] + mapRawDataIntoFormat.value.secondaryCurrentYear[i])
          / mapRawDataIntoFormat.value.totalCurrentYear) * 100);
      }
      return {
        data: [
          {
            label: 'Percentage',
            data,
            borderColor: 'pink',
            backgroundColor: 'pink',
            type: 'line',
            order: 0,
            yAxisID: 'y1',
          },
        ],
        scales: {
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            grid: {
              drawOnChartArea: false,
            },
            ticks: {
              // eslint-disable-next-line
              callback: (value: number) => `${value}%`,
            },
          },
        },
      };
    });

    const chartData = computed(() => ({
      labels: thMonthsMini,
      datasets: [
        {
          label: 'FTL (Y-1)',
          data: mapRawDataIntoFormat.value.primaryLastYear,
          backgroundColor: 'rgba(156,156,170,0.5)',
          stack: 'Y-1',
        },
        {
          label: 'LTL (Y-1)',
          data: mapRawDataIntoFormat.value.secondaryLastYear,
          backgroundColor: 'rgba(156,156,170,0.2)',
          stack: 'Y-1',
        },
        {
          label: 'FTL',
          data: mapRawDataIntoFormat.value.primaryCurrentYear,
          backgroundColor: props.primaryColor,
          stack: 'CURRENT',
        },
        {
          label: 'LTL',
          data: mapRawDataIntoFormat.value.secondaryCurrentYear,
          backgroundColor: props.secondaryColor,
          stack: 'CURRENT',
        },
        ...percentage.value.data,
      ],
    }));

    return {
      max,
      filter,
      chartData,
      rawData,
      percentage,
      plugins,
    };
  },
});

export default ChartWithLastYearCompare;
